import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ArrowLeft, Settings, Users, ArrowUpDown, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Bracket } from "@/components/Bracket";
import { RegisterDialog } from "@/components/RegisterDialog";
import { FinishesTable } from "@/pages/Leaderboard";

export default function TournamentDetail() {
  const { id } = useParams();
  const { isAdmin } = useAuth();
  const [data, setData] = useState(null);
  const [players, setPlayers] = useState([]);
  const [sort, setSort] = useState({ key: "points", dir: "desc" });

  const loadTournament = () => api.get(`/tournaments/${id}`).then((r) => setData(r.data));

  useEffect(() => {
    loadTournament();
    api.get("/players").then((r) => setPlayers(r.data));
  }, [id]);

  if (!data) return <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 text-[#525252]">Laden…</div>;

  const { tournament, entries, matches, registration_count, registration_open, registration_deadline, registration_names, high_finishes } = data;
  const nameOf = (pid) => players.find((p) => p.id === pid)?.name || "—";

  const KO_RANK = { "Winnaar": 6, "Runner-up": 5, "Laatste 4": 4, "Laatste 8": 3, "Laatste 16": 2, "Laatste 32": 1, "Laatste 64": 0 };
  const bestLegOf = (e) => {
    const legs = (e.best_legs || []).filter((l) => l > 0);
    return legs.length ? Math.min(...legs) : Infinity;
  };

  const RANK_COLS = {
    name: (e) => nameOf(e.player_id).toLowerCase(),
    group_place: (e) => e.group_place || 99,
    winners_ko_finish: (e) => KO_RANK[e.winners_ko_finish] ?? -1,
    consolation_ko_finish: (e) => KO_RANK[e.consolation_ko_finish] ?? -1,
    count_100plus: (e) => e.count_100plus || 0,
    count_140plus: (e) => e.count_140plus || 0,
    count_180: (e) => e.count_180 || 0,
    average: (e) => e.average || 0,
    legs_total: (e) => e.legs_total || 0,
    legs_won: (e) => e.legs_won || 0,
    matches_total: (e) => e.matches_total || 0,
    matches_won: (e) => e.matches_won || 0,
    best_leg: (e) => bestLegOf(e),
    points: (e) => e.points || 0,
  };

  const toggleSort = (key) => {
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: key === "name" || key === "group_place" || key === "best_leg" ? "asc" : "desc" }));
  };

  const accessor = RANK_COLS[sort.key] || RANK_COLS.points;
  const sortedEntries = [...entries].sort((a, b) => {
    const va = accessor(a);
    const vb = accessor(b);
    let cmp;
    if (typeof va === "string") cmp = va.localeCompare(vb);
    else cmp = va - vb;
    return sort.dir === "asc" ? cmp : -cmp;
  });

  const winners = matches.filter((m) => m.bracket_type === "winners");
  const consolation = matches.filter((m) => m.bracket_type === "consolation");

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-12 lg:py-16">
      <Link to="/tournaments" className="inline-flex items-center gap-1.5 text-sm text-[#525252] hover:text-[#0A0A0A] mb-6">
        <ArrowLeft className="w-4 h-4" /> Alle toernooien
      </Link>

      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <p className="font-mono text-sm text-[#D4AF37]">{tournament.name === "Finale" ? "FINALE" : `RONDE ${String(tournament.number).padStart(2, "0")}`}{tournament.date ? ` · ${tournament.date}` : ""}</p>
          <h1 className="font-heading text-4xl lg:text-5xl tracking-tighter font-black">{tournament.name}</h1>
          {registration_count > 0 && (
            <p className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-[#525252]" data-testid="registration-count">
              <Users className="w-4 h-4 text-[#D4AF37]" /> {registration_count} ingeschreven
            </p>
          )}
          {registration_deadline && (
            <p className="mt-1 text-xs text-[#A0A0A0]" data-testid="registration-deadline">
              {registration_open
                ? `Inschrijven kan tot ${tournament.time || "20:00"} op ${tournament.date}`
                : `Inschrijvingen gesloten (sluiten om ${tournament.time || "20:00"} op de toernooidag)`}
            </p>
          )}
        </div>
        <div className="flex items-center gap-3">
          <RegisterDialog
            tournamentId={id}
            tournamentName={tournament.name}
            onRegistered={loadTournament}
            registrationOpen={registration_open}
            deadline={registration_deadline}
            startTime={tournament.time || "20:00"}
            isDoubles={tournament.is_doubles}
            paymentLink={tournament.payment_link}
            entryFee={tournament.entry_fee ?? 6}
          />
          {tournament.dartconnect_link && (
            <a
              href={tournament.dartconnect_link}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="dartconnect-link"
              className="inline-flex items-center gap-2 border-2 border-[#0A0A0A] text-[#0A0A0A] px-5 py-2.5 font-semibold hover:bg-[#0A0A0A] hover:text-[#D4AF37] transition-colors"
            >
              <ExternalLink className="w-4 h-4" /> DartConnect
            </a>
          )}
          {isAdmin && (
            <Link
              to={`/admin/tournament/${id}`}
              data-testid="manage-tournament-btn"
              className="inline-flex items-center gap-2 bg-[#0A0A0A] text-[#D4AF37] px-5 py-2.5 font-semibold hover:bg-[#1A1A1A] transition-colors"
            >
              <Settings className="w-4 h-4" /> Resultaten beheren
            </Link>
          )}
        </div>
      </div>

      {registration_names && registration_names.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white border border-[#E5E5E5] p-5 md:p-6 mb-8"
          data-testid="registration-names-list"
        >
          <h2 className="flex items-center gap-2 text-base font-heading font-bold tracking-tight mb-4">
            <Users className="w-5 h-5 text-[#D4AF37]" /> Ingeschreven spelers ({registration_names.length})
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-2">
            {registration_names.map((n, i) => (
              <div
                key={i}
                data-testid={`registration-name-${i}`}
                className="flex items-center gap-2 text-sm text-[#0A0A0A] py-1 border-b border-[#F2F0EA]"
              >
                <span className="font-mono text-xs text-[#A0A0A0] w-6">{String(i + 1).padStart(2, "0")}</span>
                <span className="font-medium truncate">{n}</span>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      <Tabs defaultValue="groups">
        <TabsList className="bg-white border border-[#E5E5E5] rounded-none p-0 h-auto">
          <TabsTrigger value="groups" data-testid="tab-groups" className="rounded-none px-5 py-3 data-[state=active]:bg-[#0A0A0A] data-[state=active]:text-[#D4AF37]">
            Ranking
          </TabsTrigger>
          <TabsTrigger value="winners" data-testid="tab-winners" className="rounded-none px-5 py-3 data-[state=active]:bg-[#0A0A0A] data-[state=active]:text-[#D4AF37]">
            Winnaars KO
          </TabsTrigger>
          <TabsTrigger value="consolation" data-testid="tab-consolation" className="rounded-none px-5 py-3 data-[state=active]:bg-[#0A0A0A] data-[state=active]:text-[#D4AF37]">
            Verliezers KO
          </TabsTrigger>
          <TabsTrigger value="finishes" data-testid="tab-tour-finishes" className="rounded-none px-5 py-3 data-[state=active]:bg-[#0A0A0A] data-[state=active]:text-[#D4AF37]">
            Hoogste finishes
          </TabsTrigger>
        </TabsList>

        <TabsContent value="groups" className="mt-6">
          {sortedEntries.length === 0 ? (
            <Empty text="Nog geen ranking ingevoerd." />
          ) : (
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="bg-white border border-[#E5E5E5] overflow-x-auto" data-testid="tournament-ranking-table">
              <table className="w-full">
                <thead>
                  <tr className="bg-[#FDFBF7] border-b border-[#E5E5E5] text-[10px] uppercase tracking-wider text-[#525252]">
                    <th className="text-left py-3 px-4 w-12">#</th>
                    <SortTh label="Speler" k="name" sort={sort} onSort={toggleSort} align="left" />
                    <SortTh label="W-KO" k="winners_ko_finish" sort={sort} onSort={toggleSort} align="left" />
                    <SortTh label="V-KO" k="consolation_ko_finish" sort={sort} onSort={toggleSort} align="left" />
                    <SortTh label="100+" k="count_100plus" sort={sort} onSort={toggleSort} />
                    <SortTh label="140+" k="count_140plus" sort={sort} onSort={toggleSort} />
                    <SortTh label="180" k="count_180" sort={sort} onSort={toggleSort} />
                    <SortTh label="TOT. AVG" k="average" sort={sort} onSort={toggleSort} />
                    <SortTh label="Tot. Games" k="matches_total" sort={sort} onSort={toggleSort} />
                    <SortTh label="Games Won" k="matches_won" sort={sort} onSort={toggleSort} />
                    <SortTh label="Tot. Legs" k="legs_total" sort={sort} onSort={toggleSort} />
                    <SortTh label="Legs Won" k="legs_won" sort={sort} onSort={toggleSort} />
                    <SortTh label="Top 3 legs" k="best_leg" sort={sort} onSort={toggleSort} />
                    <SortTh label="Plaats PL" k="group_place" sort={sort} onSort={toggleSort} align="center" />
                    <SortTh label="Punten" k="points" sort={sort} onSort={toggleSort} pad="px-4" />
                  </tr>
                </thead>
                <tbody>
                  {sortedEntries.map((e, idx) => {
                    const legs = [...(e.best_legs || [])].sort((a, b) => a - b).slice(0, 3);
                    return (
                      <tr key={e.id} className="border-b border-[#E5E5E5] last:border-0 hover:bg-[#FDFBF7]" data-testid={`tournament-rank-row-${idx}`}>
                        <td className="py-2.5 px-4">
                          <span className={`inline-flex items-center justify-center w-7 h-7 font-mono font-bold text-xs ${idx === 0 ? "bg-[#D4AF37] text-[#0A0A0A]" : idx === 1 ? "bg-[#A0A0A0] text-white" : idx === 2 ? "bg-[#CD7F32] text-white" : "bg-[#F2F0EA] text-[#0A0A0A]"}`}>{idx + 1}</span>
                        </td>
                        <td className="py-2.5 px-4 font-semibold text-sm whitespace-nowrap">{nameOf(e.player_id)}</td>
                        <td className="py-2.5 px-2 text-xs">{e.winners_ko_finish || "—"}</td>
                        <td className="py-2.5 px-2 text-xs">{e.consolation_ko_finish || "—"}</td>
                        <td className="py-2.5 px-2 text-right font-mono text-sm">{e.count_100plus}</td>
                        <td className="py-2.5 px-2 text-right font-mono text-sm">{e.count_140plus}</td>
                        <td className="py-2.5 px-2 text-right font-mono text-sm">{e.count_180}</td>
                        <td className="py-2.5 px-2 text-right font-mono text-sm">{e.average}</td>
                        <td className="py-2.5 px-2 text-right font-mono text-sm text-[#525252]">{e.matches_total ?? 0}</td>
                        <td className="py-2.5 px-2 text-right font-mono text-sm text-[#525252]">{e.matches_won ?? 0}</td>
                        <td className="py-2.5 px-2 text-right font-mono text-sm text-[#525252]">{e.legs_total ?? 0}</td>
                        <td className="py-2.5 px-2 text-right font-mono text-sm text-[#525252]">{e.legs_won ?? 0}</td>
                        <td className="py-2.5 px-2 text-right font-mono text-sm text-[#525252] whitespace-nowrap">{legs.length ? legs.join(", ") : "—"}</td>
                        <td className="py-2.5 px-2 text-center font-mono text-sm">{e.group_place ? `${e.group_place}e` : "—"}</td>
                        <td className="py-2.5 px-4 text-right font-mono text-sm font-bold text-[#A8882D]">{e.points}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </motion.div>
          )}
        </TabsContent>

        <TabsContent value="winners" className="mt-6">
          {winners.length === 0 ? <Empty text="Nog geen winnaars knock-out wedstrijden ingevoerd." /> : <Bracket matches={winners} nameOf={nameOf} />}
        </TabsContent>

        <TabsContent value="consolation" className="mt-6">
          {consolation.length === 0 ? <Empty text="Nog geen verliezers knock-out wedstrijden ingevoerd." /> : <Bracket matches={consolation} nameOf={nameOf} />}
        </TabsContent>

        <TabsContent value="finishes" className="mt-6">
          <FinishesTable finishes={high_finishes} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

const Empty = ({ text }) => (
  <div className="bg-white border border-[#E5E5E5] p-12 text-center text-[#525252]">{text}</div>
);

const SortTh = ({ label, k, sort, onSort, align = "right", pad = "px-1.5" }) => {
  const active = sort.key === k;
  const itemsAlign = align === "left" ? "items-start" : align === "center" ? "items-center" : "items-end";
  const textAlign = align === "left" ? "text-left" : align === "center" ? "text-center" : "text-right";
  return (
    <th className={`py-3 ${pad} align-bottom ${textAlign}`}>
      <button
        onClick={() => onSort(k)}
        data-testid={`tsort-${k}`}
        className={`flex flex-col ${itemsAlign} gap-0.5 ${align === "left" ? "" : "ml-auto"} text-[10px] uppercase tracking-wider font-semibold leading-tight transition-colors ${
          active ? "text-[#D4AF37]" : "text-[#525252] hover:text-[#0A0A0A]"
        }`}
      >
        <span className={`block max-w-[3.25rem] break-words ${textAlign}`}>{label}</span>
        <ArrowUpDown className="w-2.5 h-2.5 shrink-0" />
      </button>
    </th>
  );
};

